import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import ProtectedRoute from './components/common/ProtectedRoute';
import AdminDashboard from './components/admin/AdminDashboard';
import SettingsPage from './pages/SettingsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import JoinWorkspace from './components/workspaces/JoinWorkspace';

/**
 * Main App Component
 * 
 * Learning: This shows:
 * - Context provider setup
 * - Conditional routing based on auth state
 * - App-level state management
 * - Admin dashboard routing
 */

const AppContent: React.FC = () => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const [currentView, setCurrentView] = useState<'dashboard' | 'admin' | 'settings' | 'forgot-password' | 'reset-password' | 'join-workspace'>('dashboard');
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  // Check URL path to determine which view to show
  useEffect(() => {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    
    if (path === '/admin') {
      setCurrentView('admin');
    } else if (path === '/settings') {
      setCurrentView('settings');
    } else if (path === '/forgot-password') {
      setCurrentView('forgot-password');
    } else if (path === '/reset-password') {
      setCurrentView('reset-password');
    } else if (path === '/join-workspace') {
      const token = searchParams.get('token');
      if (token) {
        setInviteToken(token);
        setCurrentView('join-workspace');
      } else {
        setCurrentView('dashboard');
      }
    } else {
      setCurrentView('dashboard');
    }

    // Listen for URL changes (browser back/forward buttons)
    const handlePopState = () => {
      const newPath = window.location.pathname;
      const newParams = new URLSearchParams(window.location.search);
      
      if (newPath === '/admin') {
        setCurrentView('admin');
      } else if (newPath === '/settings') {
        setCurrentView('settings');
      } else if (newPath === '/forgot-password') {
        setCurrentView('forgot-password');
      } else if (newPath === '/reset-password') {
        setCurrentView('reset-password');
      } else if (newPath === '/join-workspace') {
        const token = newParams.get('token');
        if (token) {
          setInviteToken(token);
          setCurrentView('join-workspace');
        }
      } else {
        setCurrentView('dashboard');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // After login, if there's a pending workspace invite, return to it
  useEffect(() => {
    if (isAuthenticated) {
      const pendingInvite = sessionStorage.getItem('pendingInvite');
      if (pendingInvite) {
        sessionStorage.removeItem('pendingInvite');
        window.location.href = pendingInvite;
      }
    }
  }, [isAuthenticated]);

  // Function to navigate between views
  const navigateTo = (view: 'dashboard' | 'admin' | 'settings') => {
    setCurrentView(view);
    const paths: { [key: string]: string } = {
      dashboard: '/',
      admin: '/admin',
      settings: '/settings'
    };
    window.history.pushState({}, '', paths[view]);
  };

  // Check if user is admin
  const isAdmin = user?.email === 'ranjitreddib@gmail.com';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading Yojana...</p>
        </div>
      </div>
    );
  }

  // Handle password reset routes (don't require auth)
  if (currentView === 'forgot-password') {
    return <ForgotPasswordPage />;
  }

  if (currentView === 'reset-password') {
    return <ResetPasswordPage />;
  }

  // Handle join workspace (works for both logged in and logged out users)
  if (currentView === 'join-workspace' && inviteToken) {
    return (
      <JoinWorkspace
        token={inviteToken}
        isLoggedIn={isAuthenticated}
        onLoginRequired={() => {
          // Store the invite URL and redirect to login
          sessionStorage.setItem('pendingInvite', window.location.href);
          window.location.href = '/';
        }}
        onSuccess={(workspace) => {
          // Navigate to workspaces view
          window.location.href = '/?view=workspaces';
        }}
      />
    );
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return (
    <ProtectedRoute>
      <div>
        {/* Navigation bar */}
        <div style={{
          backgroundColor: '#1f2937',
          padding: '1rem',
          display: 'flex',
          gap: '1rem',
          borderBottom: '2px solid #374151'
        }}>
          <button
            onClick={() => navigateTo('dashboard')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: currentView === 'dashboard' ? '#3b82f6' : 'transparent',
              color: 'white',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: currentView === 'dashboard' ? 'bold' : 'normal',
              transition: 'all 0.2s'
            }}
          >
            📅 Dashboard
          </button>
          <button
            onClick={() => navigateTo('settings')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: currentView === 'settings' ? '#3b82f6' : 'transparent',
              color: 'white',
              border: '1px solid #4b5563',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: currentView === 'settings' ? 'bold' : 'normal',
              transition: 'all 0.2s'
            }}
          >
            ⚙️ Settings
          </button>
          {isAdmin && (
            <button
              onClick={() => navigateTo('admin')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: currentView === 'admin' ? '#3b82f6' : 'transparent',
                color: 'white',
                border: '1px solid #4b5563',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: currentView === 'admin' ? 'bold' : 'normal',
                transition: 'all 0.2s'
              }}
            >
              📊 Admin
            </button>
          )}
        </div>

        {/* Main content */}
        {currentView === 'admin' && isAdmin ? (
          <AdminDashboard />
        ) : currentView === 'settings' ? (
          <SettingsPage />
        ) : (
          <Dashboard />
        )}
      </div>
    </ProtectedRoute>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <SubscriptionProvider>
          <div className="App">
            <AppContent />
          </div>
        </SubscriptionProvider>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
